import { toneFor, meaningFor } from '../statusClassification/statusClassification';

/**
 * Payroll-connection states of a Reports users-list row (`row.payrollStatus`)
 * and of the user report (`consumer.payrollStatus`) — clockout-service
 * `Constants.REPORTS.USERS.PAYROLL_STATUS` (= KAN-609's `connectionStateFor`).
 * Labels only: the colour of every state comes from the served classification
 * (`toneFor`), never from this file.
 */
export const PAYROLL_STATUS = Object.freeze({
    CONNECTED: 'connected',
    NOT_CONNECTED: 'not_connected',
    IN_PROGRESS: 'in_progress',
    FAILED: 'failed',
    UNRESOLVED: 'unresolved',
});

export const PAYROLL_STATUS_LABELS = Object.freeze({
    [PAYROLL_STATUS.CONNECTED]: 'Connected',
    [PAYROLL_STATUS.NOT_CONNECTED]: 'Not Connected',
    [PAYROLL_STATUS.IN_PROGRESS]: 'In Progress',
    [PAYROLL_STATUS.FAILED]: 'Failed',
    [PAYROLL_STATUS.UNRESOLVED]: 'Unresolved',
});

/** Filter value meaning "no payroll-status filter" — the sentinel the other chips use. */
export const PAYROLL_STATUS_FILTER_ALL = 'all';

/** Filter chip options for the Users report ("Payroll: All" first). */
export const PAYROLL_STATUS_CHIP_OPTIONS = Object.freeze([
    { value: PAYROLL_STATUS_FILTER_ALL, label: 'Payroll: All' },
    ...Object.values(PAYROLL_STATUS).map((value) => ({ value, label: PAYROLL_STATUS_LABELS[value] })),
]);

/**
 * Why the service sent `payrollStatus: null` (`consumer.payrollStatusWarning`,
 * REPORTS.USERS.WARNINGS on clockout-service), keyed by its wire code.
 */
export const PAYROLL_STATUS_WARNING_COPY = Object.freeze({
    sync_status_unavailable:
        'Payroll status could not be loaded: the sync lookup failed on the server. Reload to fetch it again.',
});

/**
 * Display entry for a payroll status: label from this vocabulary, tone and
 * meaning from the served classification.
 *
 *  - a known state        → its label, `toneFor(state)`
 *  - `null`               → "Unavailable" (the server could not load it), neutral
 *  - `undefined`          → "—" (payload predates the column), neutral
 *  - anything else        → "Unknown" with the raw value in the description,
 *                           tone from the catalog if the server classified it
 *                           (a state this build has no label for), else neutral
 *
 * @param {string | null | undefined} state
 * @param {string | null | undefined} [warning] the service's `payrollStatusWarning` code, read when `state` is null
 * @returns {{ key: string, label: string, tone: string, description: string, recognised: boolean, raw: string|null }}
 */
export function describePayrollStatus(state, warning) {
    if (state === undefined) {
        return {
            key: 'not_loaded',
            label: '—',
            tone: 'neutral',
            description: 'Payroll status not provided by the server.',
            recognised: false,
            raw: null,
        };
    }
    if (state === null) {
        return {
            key: 'unavailable',
            label: 'Unavailable',
            tone: 'neutral',
            description:
                PAYROLL_STATUS_WARNING_COPY[warning] || 'Payroll status could not be loaded. Retry to fetch it again.',
            recognised: false,
            raw: null,
        };
    }
    const key = String(state);
    if (Object.prototype.hasOwnProperty.call(PAYROLL_STATUS_LABELS, key)) {
        return {
            key,
            label: PAYROLL_STATUS_LABELS[key],
            tone: toneFor(key),
            description:
                meaningFor(key) || "Payroll-connection state of the user's most recently updated payroll connection.",
            recognised: true,
            raw: key,
        };
    }
    return {
        key: 'unknown',
        label: 'Unknown',
        tone: toneFor(key),
        description: `The server reported a payroll status this admin build does not recognise ("${key}").`,
        recognised: false,
        raw: key,
    };
}

/**
 * Muted suffix shown next to the pill when a member has several payroll
 * connections — the click-through goes to the user page, which lists them.
 * @param {number | null | undefined} count `row.connectionsCount`
 * @returns {string | null}
 */
export function connectionsSuffix(count) {
    if (typeof count !== 'number' || count <= 1) return null;
    return `${count} connections`;
}
